/**
 * Stream-level SSE consumer.
 *
 * `parseSseFrame` / `drainFramesFromBuffer` (in `@/lib/sse-parse`, shared from
 * `@edge/edge-ai`) operate on strings; this composes them into an async
 * generator over a `ReadableStream<Uint8Array>` — the read-loop that was
 * previously copy-pasted into every streaming hook (deep-dive, transcript
 * summary, research run, companion tool executor).
 *
 * It handles the two things a raw `getReader()` loop must get right:
 *   1. partial frames split across chunk boundaries (buffer carries over reads),
 *   2. a trailing frame with no terminating blank line (flushed on stream end).
 *
 * Cancellation is the caller's job — abort the underlying fetch and the reader
 * `read()` rejects, which propagates out of the generator. Consumers that want
 * to stop early can also `break` out of the `for await`, which runs the
 * generator's `finally` and releases the reader lock.
 */

import { drainFramesFromBuffer, parseSseFrame, type ParsedSseEvent } from '@/lib/sse-parse'

/**
 * Yield each parsed SSE event from a byte stream in order. Comment/ping lines
 * and frames with no `data:` are skipped (they yield nothing). The generator
 * completes when the stream ends.
 */
export async function* parseSseStream(
  stream: ReadableStream<Uint8Array>,
): AsyncGenerator<ParsedSseEvent, void, unknown> {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) {
        // Flush any trailing frame that never got a terminating blank line.
        const tail = buffer.trim()
        if (tail.length > 0) {
          const parsed = parseSseFrame(buffer)
          if (parsed) yield parsed
        }
        return
      }

      buffer += decoder.decode(value, { stream: true })
      const { frames, remaining } = drainFramesFromBuffer(buffer)
      buffer = remaining
      for (const frame of frames) {
        const parsed = parseSseFrame(frame)
        if (parsed) yield parsed
      }
    }
  } finally {
    reader.releaseLock()
  }
}
